import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { formatNumber } from '@bookingdei/i18n/format-number';
import { useLanguage } from '../../shell/language-context';
import { NavIcon } from '../../shell/nav-icon';
import type { NavIconName } from '../../shell/nav-icon';
import { HomeIcon } from './home-icons';
import { ATTENTION_PAGE } from './home-model';
import type { AttentionItem } from './home-model';

type Tone = 'warn' | 'info' | 'grey';

interface Row {
  key: string;
  tone: Tone;
  icon: NavIconName;
  title: string;
  detail: string;
  action?: string;
  chip?: string;
  to?: string;
}

// "Needs your attention" (M:471-517, design `att` M:10004-10012): payment proofs to
// verify, services sent back by moderation, offers about to end and payment
// applications under review — four per page with a pager. Each actionable tile routes
// to where the work is; the first one on page one is the primary (accent) button.
// SMS credit (design's fifth item) has no merchant read in the contract (DR-32).
export function HomeAttention({ items, nameOf }: { items: readonly AttentionItem[]; nameOf: (businessId: string) => string }) {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);

  const rows: Row[] = items.map((a) => {
    switch (a.kind) {
      case 'proofs':
        return {
          key: a.kind,
          tone: 'warn',
          icon: 'taka',
          title: formatMessage(lang, 'dc71.att.proofs.title', { n: a.count }),
          detail: formatMessage(lang, 'dc71.att.proofs.detail', { n: a.hours ?? 0 }),
          action: t('dc71.att.proofs.action'),
          to: '/bookings?tab=payment',
        };
      case 'fix_service':
        return {
          key: a.kind,
          tone: 'warn',
          icon: 'alert',
          title: formatMessage(lang, 'dc71.att.fix.title', { n: a.count }),
          detail: t('dc71.att.fix.detail'),
          action: t('dc71.att.fix.action'),
          to: a.itemId ? `/services?service=${encodeURIComponent(a.itemId)}` : '/services',
        };
      case 'offer_ending':
        return {
          key: a.kind,
          tone: 'info',
          icon: 'tag',
          title: formatMessage(lang, 'dc71.att.offer.title', { n: a.count }),
          detail: formatMessage(lang, 'dc71.att.offer.detail', { n: a.days ?? 0 }),
          action: t('dc71.att.offer.action'),
          to: '/offers',
        };
      case 'kyc_review':
      default:
        return {
          key: `${a.kind}-${a.businessId ?? ''}`,
          tone: 'grey',
          icon: 'clock',
          title: formatMessage(lang, 'dc71.att.kyc.title', { name: nameOf(a.businessId ?? '') }),
          detail: t('dc71.att.kyc.detail'),
          chip: t('dc71.att.kyc.chip'),
        };
    }
  });

  const pages = Math.max(1, Math.ceil(rows.length / ATTENTION_PAGE));
  const current = Math.min(page, pages - 1);
  const visible = rows.slice(current * ATTENTION_PAGE, current * ATTENTION_PAGE + ATTENTION_PAGE);
  const from = current * ATTENTION_PAGE + 1;
  const to = Math.min(rows.length, from + ATTENTION_PAGE - 1);

  return (
    <section className="hm-card" aria-labelledby="hm-att-title">
      <div className="hm-card__head">
        <h3 id="hm-att-title" className="hm-card__title">
          {t('dc71.attention.title')}
        </h3>
        {rows.length > 0 && <span className="hm-count">{formatNumber(lang, rows.length)}</span>}
        {pages > 1 && (
          <span className="hm-pager hm-push">
            <span className="hm-pager__range">
              {formatMessage(lang, 'dc71.attention.range', { from, to, total: rows.length })}
            </span>
            <button
              type="button"
              className="hm-pager__btn"
              aria-label={t('dc71.attention.prev')}
              disabled={current === 0}
              onClick={() => setPage(Math.max(0, current - 1))}
            >
              <HomeIcon name="chevLeft" size={16} strokeWidth={2} />
            </button>
            <button
              type="button"
              className="hm-pager__btn"
              aria-label={t('dc71.attention.next')}
              disabled={current >= pages - 1}
              onClick={() => setPage(Math.min(pages - 1, current + 1))}
            >
              <HomeIcon name="chevRight" size={16} strokeWidth={2} />
            </button>
          </span>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="hm-att-empty">
          <span className="hm-att__tile hm-att__tile--ok">
            <NavIcon name="checkCircle" size={18} strokeWidth={1.75} />
          </span>
          <span className="hm-att-empty__text">{t('dc71.attention.all_good')}</span>
        </div>
      ) : (
        <div className="hm-att-grid">
          {visible.map((r, i) => {
            const primary = current === 0 && i === 0 && Boolean(r.to);
            const go = r.to ? () => navigate(r.to!) : undefined;
            return (
              <div key={r.key} className={`hm-att hm-att--${r.tone}${go ? ' is-link' : ''}`} onClick={go}>
                <span className={`hm-att__tile hm-att__tile--${r.tone}`}>
                  <NavIcon name={r.icon} size={18} strokeWidth={1.75} />
                </span>
                <span className="hm-att__text">
                  <span className="hm-att__title">{r.title}</span>
                  <span className="hm-att__detail">{r.detail}</span>
                </span>
                {r.action ? (
                  <button
                    type="button"
                    className={primary ? 'hm-att__btn hm-att__btn--primary' : `hm-att__btn hm-att__btn--${r.tone}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      go?.();
                    }}
                  >
                    {r.action}
                  </button>
                ) : (
                  <span className={`hm-att__chip hm-att__chip--${r.tone}`}>{r.chip}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
